import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavParams, ModalController, IonicModule, AlertController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { AgentTopicTools, Automation } from 'src/models';
import { AgentTopicsService } from 'src/services/agent-topics.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { CustomFieldsModule } from '../custom-fields/custom-fields.module';

@Component({
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, CustomFieldsModule, NgxTippyModule, AvatarModule],
  selector: 'app-edit-topic',
  templateUrl: './edit-topic.component.html',
  styleUrls: ['./edit-topic.component.scss'],
})
export class EditTopicComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;

  id?: string;
  name: string;
  description: string;
  projectId: string;
  instructions: string;
  tools: AgentTopicTools[];

  isLoading = false;

  constructor(
    navParams: NavParams,
    private agentTopicsService: AgentTopicsService,
    private alertCtrl: AlertController,
    private msgService: MessageService,
    private modalCtrl: ModalController,
    private searchablePopoverService: SearchablePopoverService,
  ) {
    this.id = navParams.get('id');
    this.projectId = navParams.get('projectId');
  }

  ngOnInit(): void {
    if (this.id) {
      this.loadTopic();
    } else {
      setTimeout(() => this.nameInput.setFocus(), 500);
    }
  }

  arrayTrackBy(index, item) {
    return index;
  }

  addNewTool() {
    this.searchablePopoverService.automations({
      event,
      projectId: this.projectId,
      callback: (id: string, _name: string, data: Automation) => {
        const tool: any = { topicId: this.id, description: '' };
        tool.automationId = id;
        tool.automation = data;
        this.tools = (this.tools ?? []).concat(tool);
      },
    });
  }

  selectAutomation(tool) {
    this.searchablePopoverService.automations({
      event,
      projectId: this.projectId,
      selectedAutomationId: tool.automationId,
      callback: (id: string, _name: string, data: Automation) => {
        tool.automationId = id;
        tool.automation = data;
      },
    });
  }

  removeAutomation(tool) {
    this.tools = this.tools.filter((t) => t !== tool);
  }

  selectEntity(tool) {
    this.searchablePopoverService.entities({
      event,
      selectedEntityId: tool?.approverId,
      type: tool?.approverType,
      callback(id, _name, user) {
        tool.approverId = id;
        tool.approverType = user.type;
        tool.approver = user;
      },
    });
  }

  removeEntity(tool) {
    tool.approverId = undefined;
    tool.approverType = undefined;
    tool.approver = undefined;
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader: `This can't be undone`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              await this.agentTopicsService.delete(this.id);
              this.dismiss();
            } catch (e) {
              this.msgService.show(`Unable to delete topic: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async submit() {
    if (!this.name) {
      await this.msgService.show('Whoops! Please enter a name.');

      return;
    }

    this.isLoading = true;

    try {
      if (this.id) {
        await this.agentTopicsService.updatePartial(this.id, {
          name: this.name,
          description: this.description,
          projectId: this.projectId,
          instructions: this.instructions,
          tools: this.tools,
        });

        await this.msgService.show('Topic updated!');
      } else {
        await this.agentTopicsService.create({
          name: this.name,
          description: this.description,
          projectId: this.projectId,
          instructions: this.instructions,
          tools: this.tools,
        });

        await this.msgService.show('Topic added!');
      }

      this.dismiss();
    } catch (error) {
      await this.msgService.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadTopic() {
    this.isLoading = true;
    try {
      const topic = await this.agentTopicsService.getById(this.id);

      this.id = topic.id;
      this.name = topic.name;
      this.description = topic.description;
      this.instructions = topic.instructions;
      this.tools = topic.tools;
    } catch (e) {
      this.msgService.showError(e);
    } finally {
      this.isLoading = false;
    }
  }

  moveProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.projectId,
      callback: async (id: string, name: string) => {
        if (!id || id === this.projectId) {
          return;
        }

        const alert = await this.alertCtrl.create({
          header: 'Move project?',
          subHeader: 'Are you sure you want to continue?',
          buttons: [
            {
              text: 'Cancel',
            },
            {
              text: 'Continue',
              handler: async () => {
                this.isLoading = true;

                try {
                  await this.agentTopicsService.updatePartial(this.id, {
                    projectId: id,
                  });

                  this.dismiss();
                } catch (e) {
                  this.msgService.showError(e);
                } finally {
                  this.isLoading = false;
                }
              },
            },
          ],
        });
        alert.present();
      },
    });
  }

  async duplicate() {
    const alert = await this.alertCtrl.create({
      header: 'Duplicate topic?',
      subHeader: `You can confirm details before you save.`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Duplicate',
          handler: () => {
            this.id = undefined;
            setTimeout(() => {
              this.name = `Copy of ${this.name}`;
              this.nameInput.setFocus();
            }, 500);
          },
        },
      ],
    });
    alert.present();
  }
}
